import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ArrowRight, Video } from 'lucide-react';
import { adminApi } from '../api';
import { fmtDate, fmtTime, todayYmd } from '../format';
import { AdminSelect } from '../workspace-ui';
import { DatePickerField, TimePickerField } from '../pickers';
import s from '../workspace.module.css';
import { zonedWallTimeToUtcIso } from './useSortedTimezones';
import useHostGroups from './useHostGroups';
import HostDaySchedule from './HostDaySchedule';
import { US_TIMEZONES, DEFAULT_US_TZ, safeTz, utcToZonedWallTime } from '../usTimezones';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '@/components/ui/select';

const shortLink = (link) => (link || '').replace(/^https?:\/\//, '');
const tzLabel = (v) => US_TIMEZONES.find((o) => o.value === v)?.label || v;

// Edit a confirmed booking in one go: host, session type, date and time (the VA's ask — a transfer and a new time in the
// same action). One request moves the ledger row, the calendar event, the Meet co-hosts and the Practice Better session.
// The patient is emailed when the time or session changes; a host-only change stays quiet, as transfers always have.
// Date and time are in the patient's timezone, like the booking was made. If the host is already booked then, the
// dialog says with whom and offers to save anyway.
export default function EditBookingDialog({ booking: b, onClose, onDone }) {
  const hostGroups = useHostGroups();
  const [sessions, setSessions] = useState([]);
  const tz = safeTz(b.patient_timezone, DEFAULT_US_TZ);
  const [form, setForm] = useState(() => {
    const at = utcToZonedWallTime(b.slot_start_utc, tz);
    return { host_id: b.director_id || '', session_id: b.session_id || '', date: at.date, time: at.time };
  });
  const [notify, setNotify] = useState(true);
  const [busy, setBusy] = useState(null);       // the host's bookings at the new time (409 host_busy)
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminApi.get('/admin/settings').then((r) => setSessions(r.data.sessions || [])).catch(() => {});
  }, []);

  const host = hostGroups.flatMap((g) => g.items).find((h) => h.id === form.host_id);
  const session = sessions.find((x) => x.id === form.session_id);
  const slotIso = form.date && form.time ? zonedWallTimeToUtcIso(form.date, form.time, tz) : null;
  const hostChanged = !!form.host_id && form.host_id !== b.director_id;
  const sessionChanged = !!form.session_id && form.session_id !== (b.session_id || '');
  const timeChanged = !!slotIso && new Date(slotIso).getTime() !== new Date(b.slot_start_utc).getTime();
  const changed = hostChanged || sessionChanged || timeChanged;
  const patient = [b.patient?.first_name, b.patient?.last_name].filter(Boolean).join(' ') || b.patient?.email || 'This patient';
  const set = (key, value) => { setForm((f) => ({ ...f, [key]: value })); setBusy(null); };

  const sessionOptions = sessions.map((x) => ({ value: x.id, label: `${x.title} · ${x.duration_minutes} min` }));
  if (b.session_id && !sessions.some((x) => x.id === b.session_id)) sessionOptions.unshift({ value: b.session_id, label: b.session_title || 'Current session' });

  const save = async (allowOverlap = false) => {
    setSaving(true);
    try {
      const r = await adminApi.post(`/admin/bookings/${b.booking_id}/edit`, {
        host_id: hostChanged ? form.host_id : undefined,
        slot_start_time: timeChanged ? slotIso : undefined,
        session_id: sessionChanged ? form.session_id : undefined,
        notify, allow_overlap: allowOverlap,
      });
      toast.success('Booking updated',
        r.data.pb_status === 'pending' ? { description: 'Practice Better is still catching up. It retries on its own.' } : undefined);
      onDone?.();
      onClose();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail?.code === 'host_busy') setBusy(detail.existing || []);
      else toast.error(typeof detail === 'string' ? detail : (detail?.message || 'Could not update the booking'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o && !saving) onClose(); }}>
      <DialogContent className={`${s.formDialog} ${s.lyraDialog} ${s.editDialog}`}>
        <DialogHeader>
          <DialogTitle>Edit {patient}’s booking</DialogTitle>
          <DialogDescription className={s.sheetDescription}>
            Now {fmtDate(b.slot_start_utc)} at {fmtTime(b.slot_start_utc)} with {b.director_name || b.host_name || 'their host'}.
            Change any of the host, session, date and time; date and time are in the patient’s zone ({tzLabel(tz)}).
          </DialogDescription>
        </DialogHeader>
        <form noValidate onSubmit={(e) => { e.preventDefault(); if (changed && slotIso) save(Boolean(busy)); }} className={s.editLayout}>
          <div>
          <div className={`${s.fields} ${s.transferField}`}>
            <div className={`${s.field} ${s.wideField}`}>
              <label htmlFor="edit-session">Session</label>
              <AdminSelect id="edit-session" label="Session" value={form.session_id} onChange={(v) => set('session_id', v)} placeholder="Keep the current session" options={sessionOptions} />
            </div>
            <div className={`${s.field} ${s.wideField}`}>
              <label htmlFor="edit-host">Host</label>
              <Select value={form.host_id || undefined} onValueChange={(v) => set('host_id', v)}>
                <SelectTrigger id="edit-host" aria-label="Host" className={s.select}><SelectValue placeholder="Choose a host" /></SelectTrigger>
                <SelectContent position="popper" className={s.selectMenu}>
                  {hostGroups.map((g) => (
                    <SelectGroup key={g.key}>
                      <SelectLabel>{g.label}</SelectLabel>
                      {g.items.map((h) => <SelectItem key={h.id} value={h.id} className={s.selectOption}>{h.name}</SelectItem>)}
                    </SelectGroup>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className={s.field}>
              <label htmlFor="edit-date">Date</label>
              <DatePickerField id="edit-date" value={form.date} onChange={(v) => set('date', v)} today={todayYmd(tz)} />
            </div>
            <div className={s.field}>
              <label htmlFor="edit-time">Time</label>
              <TimePickerField id="edit-time" value={form.time} onChange={(v) => set('time', v)} />
            </div>
          </div>
          {b.meet_link && hostChanged && (
            <p className={s.transferLink}><Video size={13} />Keeps <span className={s.meetLink}>{shortLink(b.meet_link)}</span>{host && <> · {host.name} becomes a co-host</>}</p>
          )}

          {busy && (
            <>
              <p className={s.busyLabel}>{host?.name || 'This host'} is already booked then</p>
              <ul className={s.busyList}>
                {busy.map((x) => (
                  <li key={x.booking_id}>
                    <strong>{x.patient_name}</strong>{x.session_title && <span>· {x.session_title}</span>}
                    {x.meet_link && <span className={s.meetLink}><Video size={13} />{shortLink(x.meet_link)}</span>}
                  </li>
                ))}
              </ul>
              <p className={s.transferNote}>Save anyway and {host?.name || 'they'} will have both sessions at once.</p>
            </>
          )}

          {(timeChanged || sessionChanged) && (
            <label className={s.transferLink} style={{ cursor: 'pointer' }}>
              <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} />
              Email the patient the new details
            </label>
          )}
          {hostChanged && !timeChanged && !sessionChanged && <p className={s.transferNote}>Only the host changes, so the patient isn’t notified.</p>}

          <div className={s.dialogActions}>
            <button type="button" className={s.secondaryButton} onClick={onClose} disabled={saving}>Cancel</button>
            <button type="submit" className={s.primaryButton} disabled={saving || !changed || !slotIso}>
              {saving ? 'Saving…' : busy ? 'Save anyway' : <>Save changes <ArrowRight size={16} /></>}
            </button>
          </div>
          </div>
          <HostDaySchedule hostId={form.host_id} hostName={host?.name || b.director_name || b.host_name} date={form.date} tz={tz} slotIso={slotIso}
            durationMinutes={session?.duration_minutes || b.duration_minutes || 30} excludeBookingId={b.booking_id} />
        </form>
      </DialogContent>
    </Dialog>
  );
}
